import { nanoid } from "nanoid";
import { joinLines, mapTabs } from "../../helpers/exportHelpers";
import { IExportArtifact } from "../ExportArtifacts/IExportArtifact";
import { IModelInfo } from "../ModelInfos/IModelInfo";

export function generateCsharpDomain(models: IModelInfo[]): IExportArtifact[] {
    const artifacts: IExportArtifact[] = [];
    const tab = "    ";
    const line = "\r\n";

    // Model(s).IModelRepository
    // Model(s).IModelService
    // Model(s).ModelService
    // Model(s).Model

    for (const model of models) {
        const pluralName = `${model.name}s`
        const namespace = `Domain.${pluralName}`;

        artifacts.push(getModelClassArtifact(model, tab, line, namespace, `Domain/${pluralName}/${model.name}.cs`));
        artifacts.push(getModelIRepositoryArtifact(model, tab, line, namespace, `Domain/${pluralName}/I${model.name}Repository.cs`));
        artifacts.push(getModelIServiceArtifact(model, tab, line, namespace, `Domain/${pluralName}/I${model.name}Service.cs`));
        artifacts.push(getModelServiceArtifact(model, tab, line, namespace, `Domain/${pluralName}/${model.name}Service.cs`));
    }

    return artifacts;
}

function getModelClassArtifact(model: IModelInfo, tab: string, line: string, namespace: string, fileName: string) {
    const properties = model.members.map(x => `\tpublic ${x.type.name} ${x.name} { get; set; }`);

    const classContent = mapTabs(tab, [
        `public class ${model.name}`,
        `{`,
        ...properties,
        `}`
    ], 1);

    const usings = model.imports.map(x => `using ${x};`);

    const fileContent = joinLines(line, [
        ...usings,
        '',
        `namespace ${namespace}`,
        `{`,
        ...classContent,
        `}`,
        ''
    ]);

    const artifact: IExportArtifact = {
        id: nanoid(),
        exportId: nanoid(),
        created: new Date(),
        fileName: fileName,
        content: fileContent
    };
    return artifact;
}

function getModelIRepositoryArtifact(model: IModelInfo, tab: string, line: string, namespace: string, fileName: string) {
    const properties = [
        `${tab}Task<IEnumerable<${model.name}>> GetAll();`
    ];

    const classContent = mapTabs(tab, [
        `public interface I${model.name}Repository`,
        `{`,
        ...properties,
        `}`
    ], 1);

    const allUsings = new Set<string>([...model.imports, "System", "System.Threading", "System.Collections.Generic"]);
    const usings = Array.from(allUsings).map(x => `using ${x};`);

    const fileContent = joinLines(line, [
        ...usings,
        '',
        `namespace ${namespace}`,
        `{`,
        ...classContent,
        `}`,
        ''
    ]);

    const artifact: IExportArtifact = {
        id: nanoid(),
        exportId: nanoid(),
        created: new Date(),
        fileName: fileName,
        content: fileContent
    };
    return artifact;
}


function getModelIServiceArtifact(model: IModelInfo, tab: string, line: string, namespace: string, fileName: string) {
    const properties = [
        `${tab}Task<IEnumerable<${model.name}>> GetAll();`
    ];

    const classContent = mapTabs(tab, [
        `public interface I${model.name}Service`,
        `{`,
        ...properties,
        `}`
    ], 1);

    const allUsings = new Set<string>([...model.imports, "System", "System.Threading", "System.Collections.Generic"]);
    const usings = Array.from(allUsings).map(x => `using ${x};`);

    const fileContent = joinLines(line, [
        ...usings,
        '',
        `namespace ${namespace}`,
        `{`,
        ...classContent,
        `}`,
        ''
    ]);

    const artifact: IExportArtifact = {
        id: nanoid(),
        exportId: nanoid(),
        created: new Date(),
        fileName: fileName,
        content: fileContent
    };
    return artifact;
}

function getModelServiceArtifact(model: IModelInfo, tab: string, line: string, namespace: string, fileName: string) {
    const properties = [
        `${tab}private readonly I${model.name}Repository Repository;`,
        `${tab}public ${model.name}Service(I${model.name}Repository repository) {`,
        `${tab}${tab}Repository = repository;`,
        `${tab}}`,
        `${tab}public Task<IEnumerable<${model.name}>> GetAll() => Repository.GetAll()`
    ];

    const classContent = mapTabs(tab, [
        `public class ${model.name}Service : I${model.name}Service`,
        `{`,
        ...properties,
        `}`
    ], 1);

    const allUsings = new Set<string>([...model.imports, "System", "System.Threading", "System.Collections.Generic"]);
    const usings = Array.from(allUsings).map(x => `using ${x};`);

    const fileContent = joinLines(line, [
        ...usings,
        '',
        `namespace ${namespace}`,
        `{`,
        ...classContent,
        `}`,
        ''
    ]);

    const artifact: IExportArtifact = {
        id: nanoid(),
        exportId: nanoid(),
        created: new Date(),
        fileName: fileName,
        content: fileContent
    };
    return artifact;
}